import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { 
  Bell, Mail, Calendar, AlertTriangle, Clock, CheckCircle2, 
  XCircle, Send, Loader2, Save, Plus, X, History
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Paris'
  });
};

export default function AlertesSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    recipients: [],
    ago_alerts: true,
    comptes_alerts: true,
    kbis_alerts: true,
    registres_alerts: true,
    ago_thresholds: [60, 30, 15, 7, 1],
    comptes_thresholds: [30, 15, 7, 1],
    app_url: ""
  });
  const [newRecipient, setNewRecipient] = useState("");
  const [history, setHistory] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/alerts/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, [token]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/alerts/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.logs || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [token]);

  // Fetch upcoming deadlines
  const fetchDeadlines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/alerts/upcoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingDeadlines(res.data.deadlines || []);
    } catch (error) {
      console.error("Error fetching deadlines:", error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchHistory(), fetchDeadlines()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSettings, fetchHistory, fetchDeadlines]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/corporate/alerts/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Send test alert
  const handleTest = async () => {
    if (settings.recipients.length === 0) {
      toast.error("Ajoutez au moins un destinataire");
      return;
    }
    
    setTesting(true);
    try {
      await axios.post(`${API}/api/corporate/alerts/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Email de test envoyé !");
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setTesting(false);
    }
  };

  // Add recipient
  const addRecipient = () => {
    if (!newRecipient || !newRecipient.includes("@")) {
      toast.error("Entrez une adresse email valide");
      return;
    }
    if (settings.recipients.includes(newRecipient)) {
      toast.error("Cette adresse est déjà dans la liste");
      return;
    }
    setSettings(prev => ({
      ...prev,
      recipients: [...prev.recipients, newRecipient]
    }));
    setNewRecipient("");
  };

  // Remove recipient
  const removeRecipient = (email) => {
    setSettings(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "overdue": return "bg-red-100 text-red-700 border-red-200";
      case "urgent": return "bg-orange-100 text-orange-700 border-orange-200";
      case "warning": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default: return "bg-green-100 text-green-700 border-green-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "overdue": return <XCircle className="w-4 h-4" />;
      case "urgent": return <AlertTriangle className="w-4 h-4" />;
      case "warning": return <Clock className="w-4 h-4" />;
      default: return <CheckCircle2 className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-orange-100 rounded-xl">
            <Bell className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Alertes Juridiques</h2>
            <p className="text-sm text-gray-500">
              Recevez des rappels pour vos échéances légales
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleTest}
            disabled={testing || settings.recipients.length === 0}
            className="gap-2"
            data-testid="send-test-btn"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Tester
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="gap-2"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Main toggle */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">Activer les alertes</p>
                    <p className="text-sm text-gray-500">Recevoir des notifications par email</p>
                  </div>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                  data-testid="enable-alerts-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Recipients */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-gray-500" />
                Destinataires
              </CardTitle>
              <CardDescription>Les adresses email qui recevront les alertes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@exemple.fr"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addRecipient()}
                  data-testid="recipient-input"
                />
                <Button onClick={addRecipient} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Ajouter
                </Button>
              </div>
              
              {settings.recipients.length === 0 ? (
                <p className="text-sm text-gray-500 italic">Aucun destinataire configuré</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {settings.recipients.map((email) => (
                    <Badge 
                      key={email} 
                      variant="secondary" 
                      className="gap-1 py-1 px-3"
                    >
                      {email}
                      <button 
                        onClick={() => removeRecipient(email)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alert Types */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Types d'alertes</CardTitle>
              <CardDescription>Choisissez les alertes que vous souhaitez recevoir</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Échéance AGO</p>
                  <p className="text-sm text-gray-500">Rappels avant la date limite de l'assemblée générale</p>
                </div>
                <Switch
                  checked={settings.ago_alerts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ago_alerts: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Dépôt des comptes</p>
                  <p className="text-sm text-gray-500">Rappels pour le dépôt au greffe après l'AGO</p>
                </div>
                <Switch
                  checked={settings.comptes_alerts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, comptes_alerts: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Renouvellement Kbis</p>
                  <p className="text-sm text-gray-500">Rappel tous les 3 mois pour obtenir un Kbis récent</p>
                </div>
                <Switch
                  checked={settings.kbis_alerts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, kbis_alerts: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Mise à jour des registres</p>
                  <p className="text-sm text-gray-500">Rappel mensuel pour mettre à jour les registres légaux</p>
                </div>
                <Switch
                  checked={settings.registres_alerts}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, registres_alerts: checked }))}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel */}
        <div className="space-y-6">
          {/* Upcoming Deadlines */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                Prochaines échéances
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingDeadlines.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucune échéance à venir
                </p>
              ) : (
                <div className="space-y-3">
                  {upcomingDeadlines.map((deadline, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg border ${getStatusColor(deadline.status)}`}
                    >
                      <div className="flex items-start gap-2">
                        {getStatusIcon(deadline.status)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{deadline.title}</p>
                          <p className="text-xs opacity-80">{deadline.description}</p>
                          <p className="text-xs mt-1 font-semibold">
                            {deadline.deadline}
                            {deadline.days_remaining !== null && (
                              <span className="ml-2">
                                ({deadline.days_remaining > 0 
                                  ? `${deadline.days_remaining}j restants` 
                                  : `${Math.abs(deadline.days_remaining)}j de retard`})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-gray-500" />
                Historique des envois
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun envoi récent
                </p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {history.map((log, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="text-xs text-gray-500">{formatDate(log.sent_at)}</p>
                      <p className="font-medium mt-1">
                        {log.alerts?.length || 0} alerte(s) envoyée(s)
                      </p>
                      <p className="text-xs text-gray-500">
                        → {log.recipients?.join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
